<template>
<div>
<div v-if="!packingSlip && !showPrint" class="order-detail">
	<div :class="['detail-heading', orderDetail.order.order_method_code == 'LAYAWAY' ? 'layawayz' : '']">
		<div><label>Number</label><strong>{{ orderDetail.order.order_number }}</strong></div>
		<div><label>Date</label>{{ orderDetail.order.display_date }}</div>
		<div><label>Time</label>{{ orderDetail.order.display_time }}</div>
        <div v-if="orderDetail.order.order_method_code == 'LAYAWAY' && orderDetail.order.display_date_completed"><label>Completed</label>{{ orderDetail.order.display_date_completed }}</div>
		<div v-else><label>Type</label>{{ orderDetail.order.order_method_code.capitalizeWords() }}</div>
		<div><label>Order Total</label>${{ orderTotal }}</div>
		<div><label>Payments</label>${{ paymentTotal }}</div>
		<div><label>Balance</label>${{ orderDetail.totals.balance_remaining }}</div>
	</div>

	<div class="detail-columns">
		<div>
			<h2><div v-if="enableEdit" class="button icon" @click="$emit('edit', 'customer')"><i class="fas fa-edit"></i></div>Customer</h2>
			<div v-if="defaultCustomer">
				Default Customer <label>Anonymous Sale</label>
			</div>
			<div v-else>
				<div>{{ $fullName(orderDetail.billing) }}</div>
				<div v-if="orderDetail.billing.phone_number" style="margin: 0.5em 0 0 0;">
					<label>Phone</label>
					<div>{{ orderDetail.billing.phone_number }}</div>
				</div>
				<div v-if="orderDetail.billing.email_address" style="margin: 0.5em 0 0;">
					<label>Email</label>
					<div>{{ orderDetail.billing.email_address }}</div>
				</div>
			</div>
		</div>
		<div>
			<h2><div v-if="enableEdit" class="button icon" @click="$emit('edit', 'billing')"><i class="fas fa-edit"></i></div>Billing</h2>
			<div v-if="defaultCustomer">
				Default Customer <label>Anonymous Sale</label>
			</div>
			<div v-else>
				<div>{{ orderDetail.billing.first_name + ' ' + orderDetail.billing.middle_name + ' ' + orderDetail.billing.last_name }}</div>
				<div v-if="orderDetail.billing.company_name">{{ orderDetail.billing.company_name }}</div>
				<div>{{ orderDetail.billing.address_1 }}</div>
				<div v-if="orderDetail.billing.address_2">{{ orderDetail.billing.address_2 }}</div>
				<div>{{ orderDetail.billing.city }}, {{ orderDetail.billing.state }} {{ orderDetail.billing.zip_code }}</div>
				<div v-if="!enableEdit" class="no-print" style="margin: 1em 0 0 0;">
					<a :href="$mapURLFromAddress(orderDetail.billing)" target='_blank'><i class="fas fa-map-marker-alt"></i> Map</a>
				</div>
				<div v-if="orderDetail.custom_fields">
					<div v-for="(field, index) in orderDetail.custom_fields" style="margin: 1em 0 0 0;">
						<label>{{ field.label }}</label>
						<div>{{ field.value }}</div>
					</div>
				</div>
			</div>
		</div>
		<div>
			<h2><div v-if="enableEdit" class="button icon" @click="$emit('edit', 'shipping')"><i class="fas fa-edit"></i></div>Shipping</h2>
			<div v-if="orderDetail.shipping.full_name">
				<div>{{ orderDetail.shipping.full_name }}</div>
				<div v-if="orderDetail.shipping.company_name">{{ orderDetail.shipping.company_name }}</div>
				<div>{{ orderDetail.shipping.address_1 }}</div>
				<div v-if="orderDetail.shipping.address_2">{{ orderDetail.shipping.address_2 }}</div>
				<div>{{ orderDetail.shipping.city }}, {{ orderDetail.shipping.state }} {{ orderDetail.shipping.zip_code }}</div>
				<div class="no-print" style="margin: 1em 0 0 0;">
					<button v-if="orderDetail.shipping.shipment_id" class="button action smaller" style="margin: 0 1em 0 0;" @click="manageShipment()">Manage Shipment</button>
					<a :href="mapURL(orderDetail.shipping)" target='_blank'><i class="fas fa-map-marker-alt"></i> Map</a>
				</div>
			</div>
			<div v-else>
				In store pickup
			</div>
		</div>
		<div>
			<label>Status <i v-if="savingStatus" class="fa fa-spinner fa-spin"></i></label>
			<select v-if="orderStatusCodes" v-model="orderStatusId" :disabled="enableEdit" @change="showOrderStatusModal = true">
				<option v-for="(orderStatus, index) in orderStatusCodes" :value="orderStatus.order_status_id">
					{{ orderStatus.description }}
				</option>
			</select>
			<span v-else>{{ orderDetail.order.order_status_code }}</span>
			<div v-if="orderSources">
				<label>Source <i v-if="savingSource" class="fa fa-spinner fa-spin"></i></label>
				<select v-model="sourceId" @change="setOrderSource()">
					<option value="">Select...</option>
					<option v-for="(orderSource, index) in orderSources" :value="orderSource.source_id">
						{{ orderSource.description }}
					</option>
				</select>
			</div>
		</div>
	</div>
	<div>
		<h2><div v-if="enableEdit && editLevel == 'all'" class="button icon" @click="$emit('edit', 'payments')"><i class="fas fa-edit"></i></div>Payments</h2>
		<div v-for="(payment, index) in orderDetail.payments" class="payment_line">
			<label>Date</label>
			<label>Type / Number</label>
			<label>Auth Code / Trans ID</label>
			<label>Cashier / Register Name</label>
			<label class="right">Amount</label>
			<div>{{ payment.display_date }}</div>
			<div>
				{{ payment.card_type }} {{ payment.description }} {{ payment.amount < 0 ? 'Refund' : '' }}
				<div v-if="payment.reference_number">{{ payment.reference_number }}</div>
				<!-- <span v-if="payment.rounding_amount" class="note">(rounding ${{ payment.rounding_amount.amount }})</span> -->
                <div v-if="payment.restock_fee" class="note">Restock Fee: ${{ payment.restock_fee.amount }}</div>
			</div>
			<div>{{ payment.authorization_code }}<br>{{ payment.transaction_identifier }}</div>
			<div>{{ payment.user_name }}<br>{{ payment.device_nickname }}</div>
			<div class="right">${{ getPaymentTotal(payment)}}</div>
			<div v-if="payment.external_fee"></div>
			<div v-if="payment.external_fee" style="grid-column: 2 / -1; margin: 0.5em 0 0 0;"><router-link :to="'/reports/payment-fees-external?start=' + $moment(orderDetail.order.order_date).format('YYYY-MM-DD') + '&end=' + $moment(orderDetail.order.order_date).format('YYYY-MM-DD')"><i>Note: card service fee ${{ payment.external_fee }}, total charged ${{ (parseFloat(payment.amount) + parseFloat(payment.external_fee)).toFixed(2) }}</i></router-link></div>
		</div>
	</div>

	<div class="list item-list">
		<table>
			<thead>
				<tr>
					<th class="list"><div v-if="enableEdit" class="button icon" @click="$emit('edit', 'items')"><i class="fas fa-edit" style="font-size: 1.25em;"></i></div></th>
					<th class="list">Item</th>
					<th class="list">Source</th>
					<th class="list extra-menu"></th>
					<th class="list right">Price</th>
					<th class="list right">Qty</th>
					<th class="list right">Ext</th>
					<th class="list extra-menu"></th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(item, index) in localItems" class="item-line" :key="'item_' + index">
					<td class="list center"><router-link :to="'/inventory/manage-products/detail/' + item.product_id + '?return=' + $route.path"><div style="width: 3em; height: 3em;"><img :src="item.image_src" style="max-width: 3em; max-height: 3em;"></div></router-link></td>
					<td class="list">
						<div v-if="item.product_type_code == 'CLASSES'">
							<label>{{ item.upc ? 'UPC' : 'ID' }}</label><router-link :to="'/range-memberships/classes/' + item.class_detail_id + '?return=' + $route.path">{{ item.upc || item.product_id }}</router-link>
							<div class="item-description"><label>Class</label><router-link :to="'/range-memberships/classes/' + item.class_detail_id + '?return=' + $route.path">{{ item.description }}</router-link></div>
							<div>
								<label>{{ item.other_class_datetime ? 'Dates' : 'Date' }}</label>
								{{ item.class_datetime }}
								<template v-if="item.other_class_datetime && item.other_class_datetime.length">
									<div
										v-for="(otherClass, index) in item.other_class_datetime" :key="index">
											<label>{{ item.other_class_datetime ? 'Dates' : 'Date' }}</label>
											{{ otherClass }}
									</div>
								</template>
							</div>
							<div v-for="(classItem, index) in item.class_items">
								<label>{{ classItem.canceled ? 'Canceled' : 'Participant ' }}</label>{{ classItem.first_name }} {{ classItem.last_name }}<span v-if="classItem.email_address">, {{ classItem.email_address }}</span><span v-if="classItem.phone_number">, {{ classItem.phone_number }}</span>
							</div>
						</div>
						<div v-else-if="item.product_type_code == 'RANGE'">
							<label>{{ item.upc ? 'UPC' : 'ID' }}</label><router-link :to="'/range-memberships/range-setup/' + item.product_id + '?return=' + $route.path">{{ item.upc || item.product_id }}</router-link>
							<div class="item-description"><label>Range</label><router-link :to="'/range-memberships/range-setup/' + item.product_id + '?return=' + $route.path">{{ item.description }} {{ item.range_lane }}</router-link></div>
							<div><label>Date</label>{{ item.reservation_display_date }}</div>
						</div>
						<div v-else>
							<label>{{ item.upc ? 'UPC' : 'ID' }}</label>
							<span v-if="enableEdit">{{ item.upc || item.product_id }}</span>
							<router-link v-else :to="'/inventory/manage-products/detail/' + item.product_id + '?return=' + $route.path">{{ item.upc || item.product_id }}</router-link>
							<div class="item-description">
								<span v-if="enableEdit">{{ item.description }}</span>
								<router-link v-else :to="'/inventory/manage-products/detail/' + item.product_id + '?return=' + $route.path">{{ item.description }}</router-link>
							</div>
						</div>
						<div v-if="item.notes"><label>Note</label>{{ checkJsonParse(item.notes) }}</div>
						<div v-if="item.discount_amount"><label>Discount</label>${{ item.discount_amount }}, {{ item.override_reason }}</div>
						<div v-if="item.serial_number" style="padding: 0 0 0 1em;"><label>SN</label>{{ item.serial_number }}</div>
						<div v-if="item.override_notes"><label>Note / Type</label>{{ item.override_notes }}</div>
						<div v-if="item.user_name"><label>Manager User</label>{{ item.user_name }}</div>
						<div v-else-if="item.manager_code"><label>Manager Code</label>{{ item.manager_code.replace(/.(?=.)/g, "*") }}</div>
					</td>
					<td class="list">{{ item.distributor_code || 'Store' }} <label>({{ item.available }} Available) <tool-tip left='-12' content='The availability when this order was created (not real time)'/></label><br>${{ item.order_cost }}</td>
					<td class="list center" v-if="enableEdit"></td>
					<td v-else class="list extra-menu center" @click="getDistributorDetail(item)">
						<i :class="['fas fa-truck', 'fulfillmnet-icon', item.order_fulfillment_event_type_code]"></i>
						<div :class="['extra-menu-label', item.order_fulfillment_event_type_code]">{{ (item.order_fulfillment_event_type_code || 'Manual').capitalizeWords() }}</div>
					</td>
					<td class="list right">${{ item.order_price }}</td>
					<td class="list right">{{ item.quantity }}</td>
					<td class="list right">${{ (item.quantity * item.order_price).toFixed(2) }}</td>
					<td class="list center" v-if="enableEdit"></td>
					<td class="list extra-menu right" v-else @click="addToCart(item)">
						<i v-if="!item.client_id" :class="['fas fa-shopping-cart', { adding_to_cart: item.adding_to_cart }, { in_cart: item.in_cart }]"></i>
						<div v-if="!item.client_id" class="extra-menu-label">{{ item.in_cart ? 'In Cart' : 'Add To Cart' }}</div>
					</td>
				</tr>
				<tr class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;"><label style="position: absolute; top: 0.75em; right: 0;">Subtotal</label></td>
					<td class="list right">${{ orderDetail.totals.order_subtotal }}</td>
					<td class="list extra-menu"></td>
				</tr>
				<tr class="total-line" v-for="(discount, index) in orderDetail.discounts">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;">
						<label><i v-if="discount.notes" class="fa fa-sticky-note pointer no-print" style="line-height: inherit;" @mouseover="showDiscountNotes = true" @mouseleave="showDiscountNotes = false" /> {{ discount.description }}</label>
						<div v-if="showDiscountNotes" :style="'position: absolute; z-index: 1000; top: -0.25em; right: 10em; width: 27em; padding: 0.5em; border: 1px solid #ccc; background-color: #fff; line-height: 0.9em;'">
							{{ discount.notes }}
							<div v-if="discount.user_name">Manager User: {{ discount.user_name }}</div>
							<div v-else-if="discount.manager_code">Manager Code: {{ discount.manager_code.replace(/.(?=.)/g, "*") }}</div>
						</div>
					</td>
					<td class="list right">-${{ discount.amount }}</td>
					<td class="list right no-print"></td>
				</tr>
				<tr class="total-line" v-for="(fee, index) in orderDetail.fees">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;"><label style="position: absolute; top: 0.75em; right: 0; width: 20em;">{{ fee.description }}</label></td>
					<td class="list right">${{ fee.amount }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;"><label style="position: absolute; top: 0.75em; right: 0; width: 10em;">Shipping</label></td>
					<td class="list right">${{ orderDetail.totals.shipping_charge }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list" style="position: relative;">
						<div v-if="enableEdit && editLevel == 'all'" class="button icon" style="text-align: left; position: absolute; top: -2em; right: 10em; width: 20em; line-height: 2em;">
							<div @click="$emit('edit', 'extra')"><i class="fas fa-edit"></i> <span style="color: #333;">Shipping, Handling, Tax</span></div>
							<div @click="$emit('edit', 'discounts')"><i class="fas fa-edit"></i> <span style="color: #333;">Discounts</span></div>
							<div @click="$emit('edit', 'fees')"><i class="fas fa-edit"></i> <span style="color: #333;">Fees</span></div>
						</div>
					</td>
					<td class="list right" style="position: relative;"><label style="position: absolute; top: 0.75em; right: 0; width: 10em;">Handling</label></td>
					<td class="list right">${{ orderDetail.totals.handling_charge }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;">
                        <label style="position: absolute; top: 0.75em; right: 0; width: 10em;"><i v-if="orderDetail.order_tax_reason && orderDetail.order_tax_reason.description" class="fa fa-sticky-note pointer no-print" style="line-height: inherit;" @mouseover="showTaxReason = true" @mouseleave="showTaxReason = false" /> Sales Tax</label>
						<div v-if="showTaxReason" :style="'position: absolute; z-index: 1000; top: -0.25em; right: 10em; width: 27em; padding: 0.5em; border: 1px solid #ccc; background-color: #fff; line-height: 0.9em;'">
							<div style="text-align: right;">
								{{ orderDetail.order_tax_reason.description }}{{ orderDetail.order_tax_reason.notes ? ': ' + orderDetail.order_tax_reason.notes : '' }}
							</div>
						</div>
                    </td>
					<td class="list right">${{ orderDetail.totals.tax_charge }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr v-if="orderDetail.totals.other_tax_amount" class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;">
						<label style="position: absolute; top: 0.75em; right: 0; width: 10em;"><i class="fa fa-sticky-note pointer no-print" style="line-height: inherit;" @mouseover="showOtherFees = true" @mouseleave="showOtherFees = false" /> Other Taxes / Fees</label>
						<div v-if="showOtherFees" :style="'position: absolute; z-index: 1000; top: -0.25em; right: 10em; width: 27em; padding: 0.5em; border: 1px solid #ccc; background-color: #fff; line-height: 0.9em;'">
							<div v-for="orderItemFee in orderDetail.item_fees" class="two-columns" style="grid-template-columns: 1fr 3em; text-align: right;">
								<div>{{ orderItemFee.description }}</div>
								<div>${{ orderItemFee.amount }}</div>
							</div>
						</div>
					</td>
					<td class="list right">${{ orderDetail.totals.other_tax_amount }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr v-if="orderDetail.totals.restock_fee_total" class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;"><label style="position: absolute; top: 0.75em; right: 0; width: 10em;">Restocking Fee</label></td>
					<td class="list right">${{ orderDetail.totals.restock_fee_total }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr v-if="orderDetail.totals.cash_rounding_total" class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;"><label style="position: absolute; top: 0.75em; right: 0; width: 10em;">Cash Rounding</label></td>
					<td class="list right">${{ orderDetail.totals.cash_rounding_total }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr class="total-line">
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list right" style="position: relative;"><label style="position: absolute; top: 0.75em; right: 0; width: 10em;">Total</label></td>
					<td class="list right">${{ orderTotal }}</td>
					<td class="list right extra-menu"></td>
				</tr>
				<tr class="total-line">
					<td v-if="enableEdit && editLevel == 'all'"></td>
					<td v-else style="position: relative;">
						<div class="order-notes-input" style="position: absolute; bottom: 2em; left: 1em; width: 30em;">
							<textarea style="resize: none; height: 4em; margin: 0;" v-model="orderDetail.order.notes"></textarea>
							<button :class="[ savingNotes ? 'disabled' : 'action', 'smaller']" @click="saveOrderNotes()">Save Notes</button>
						</div>
						<div v-if="orderDetail.order.notes" class="order-notes-display" style="position: absolute; bottom: 2em; left: 1em; width: 30em;">
							<label style="display: block;">Notes</label>
							<div>{{ orderDetail.order.notes }}</div>
						</div>
					</td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list extra-menu"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
					<td class="list"></td>
				</tr>
			</tbody>
		</table>
	</div>

	<div class="modal" v-if="showOrderLogModal">
		<div class="inner">
			<h2>Distributor Order Log</h2>
			<div class="content">
				<div v-for="(fulfillmentEvent, index) in fulfillmentEvents">
					<pre>{{ fulfillmentEvent.order_fulfillment_event_type_code }}: {{ fulfillmentEvent.order_fulfillment_event_time }}</pre>
					<pre>{{ fulfillmentEvent.content }}</pre>
					<pre>==========================================================================</pre>
				</div>
				<div v-if="modalMessage" class="empty">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel" @click="showOrderLogModal = false">Close</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showOrderStatusModal">
		<div class="inner">
			<h2>Change Status to {{ orderStatusCodes[orderStatusCodes.findIndex(statusCode => statusCode.order_status_id == orderStatusId)].order_status_code }}</h2>
			<div class="content">
				<p>Order status is <b>ONLY</b> used to help you filter your list of orders.<br>It doesn't change inventory or refund payments.</p>
				<p v-if="orderStatusCodes[orderStatusCodes.findIndex(statusCode => statusCode.order_status_id == orderStatusId)].order_status_code == 'Returned'">Use "Record Return Sale" or your POS to actually return an order.</p>
				<p v-if="orderStatusCodes[orderStatusCodes.findIndex(statusCode => statusCode.order_status_id == orderStatusId)].order_status_code == 'Canceled'">Use "Record Return Sale" or your POS to actually return an order.</p>
				<div v-if="modalMessage" class="empty">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="orderStatusId = orderDetail.order.order_status_id; showOrderStatusModal = false">Cancel</div>
				<div :class="['button', savingStatus ? 'disabled' : 'action', 'spaced']" @click="setOrderStatus()">Change Status</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showMoreInfoModal">
		<div class="inner" style="max-height: 33em;">
			<h2>Info Required</h2>
			<div class="content" style="max-height: 33em;">
				<div class="message center" style="margin: 0 0 1em 0;">Enter the receiving FFL's info for this firearms drop-ship request.</div>
				<label>FFL Company Name</label>
				<input type="text" v-model="infoRequired.company_name" />
				<label>Address</label>
				<input type="text" v-model="infoRequired.address_1" />
				<label>Address 2</label>
				<input type="text" v-model="infoRequired.address_2" />
				<div class="three-columns" style="grid-template-columns: 1.5fr 1.5fr 1fr;">
					<label>City</label>
					<label>State</label>
					<label>Zip Code</label>
					<input type="text" v-model="infoRequired.city" />
					<state-select type="text" :selectedState="infoRequired.state" placeHolder="Select..." @updateSelected="updateSelectedState"></state-select>
					<input type="text" v-model="infoRequired.zip_code" />
				</div>
				<div class="three-columns" style="grid-template-columns: 1.5fr 1.5fr 1fr;">
					<label>Phone Number</label>
					<label>FFL Number</label>
					<label>FFL Expires</label>
					<input type="text" v-model="infoRequired.phone_number" />
					<input type="text" v-model="infoRequired.ffl_number" />
					<input type="text" v-model="infoRequired.ffl_expires" />
				</div>
				<div class="message center">{{ modalMessage || 'This will create a new distributor order with FFL drop ship info.' }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showMoreInfoModal = false">Cancel</div>
				<div :class="['button', submittingOrder ? 'disabled' : 'action', 'spaced']" @click="submitInfoRequested()">Submit Order</div>
			</div>
		</div>
	</div>

</div>

<div v-if="packingSlip" id="packing-slip">
	<div style="font-family: Helvetica, Arial, sans-serif; font-size: 14px;">
		<h2>Packing Slip</h2>
		<table style="width: 100%; font-size: 14px;">
			<tr>
				<td>
					{{ getClient.company_name }}<br>
					{{ getClient.address_1 }}<br>
					{{ getClient.city }} {{ getClient.state }} {{ getClient.zip_code }}<br>
					<div v-if="getClient.phone_number">
						<label>Phone</label>{{ getClient.phone_number }}
					</div>
					<div v-if="getClient.email_address">
						<label>Email</label>{{ getClient.email_address }}
					</div>
					<h3 style="margin: 1em 0 0 0;">Bill To</h3>
					<div v-if="defaultCustomer">
						Default Customer <label>Anonymous Sale</label>
					</div>
					<div v-else>
						<div>{{ orderDetail.billing.first_name + ' ' + orderDetail.billing.last_name }}</div>
						<div>{{ orderDetail.billing.address_1 }}</div>
						<div>{{ orderDetail.billing.city }} {{ orderDetail.billing.state }} {{ orderDetail.billing.zip_code }}</div>
						<div v-if="orderDetail.billing.phone_number">
							<label style="margin: 0.5em 0 0 0;">Phone</label>{{ orderDetail.billing.phone_number }}
						</div>
						<div v-if="orderDetail.billing.email_address">
							<label style="margin: 0.5em 0 0 0;">Email</label>{{ orderDetail.billing.email_address }}
						</div>
					</div>

					<div>
						<h3 style="margin: 1em 0 0 0;">Shipping</h3>
						<div v-if="orderDetail.shipping.full_name">
							<div>{{ orderDetail.shipping.full_name }}</div>
							<div v-if="orderDetail.shipping.company_name">{{ orderDetail.shipping.company_name }}</div>
							<div>{{ orderDetail.shipping.address_1 }}</div>
							<div v-if="orderDetail.shipping.address_2">{{ orderDetail.shipping.address_2 }}</div>
							<div>{{ orderDetail.shipping.city }}, {{ orderDetail.shipping.state }} {{ orderDetail.shipping.zip_code }}</div>
						</div>
						<div v-else>
							In store pickup
						</div>
					</div>
					<div v-if="orderDetail.order.notes">
						<h3 style="margin: 1em 0 0 0;">Note</h3>
						<div>{{ orderDetail.order.notes }}</div>
					</div>
				</td>
				<td>
					<div class="logo-wrapper">
						<img v-if="orderDetail.order.logo_src" :src="orderDetail.order.logo_src" style="max-width: 90px;" />
					</div>
					<table class="items" style="width: 100%; font-size: 14px;">
						<tr>
							<td class="label">Order #</td><td>{{ orderDetail.order.order_number }}</td>
						</tr>
						<tr>
							<td class="label">Date</td><td>{{ orderDetail.order.order_date }}</td>
						</tr>
					</table>
				</td>
			</tr>
		</table>
		<table class="items" style="width: 100%; font-size: 14px;">
			<tr>
				<td class="label">Item</td><td class="label">UPC / Serial #</td><td class="label">Quantity</td>
			</tr>
			<tr v-for="(item, index) in localItems">
				<td>{{ item.full_description }}</td>
				<td>{{ item.upc }} <span v-if="item.serial_number"><label>SN</label>{{ item.serial_number }}</span></td>
				<td>{{ item.quantity }}</td>
			</tr>
		</table>
	</div>
</div>

<div v-if="showPrint" id="packing-slip">
	<div style="font-family: Helvetica, Arial, sans-serif; font-size: 14px;">
		<table v-if="orderDetail.exclude_cost" style="width: 100%; font-size: 14px;">
			<tr>
				<td style="vertical-align: bottom;">
					<div><img :src="orderDetail.order.logo_src" style="max-width: 100px;" /></div>
				</td>
				<td style="vertical-align: bottom;">
					{{ getClient.company_name }}<br>
					{{ getClient.address_1 }}<br>
					{{ getClient.city }} {{ getClient.state }} {{ getClient.zip_code }}<br>
				</td>
				<td style="vertical-align: bottom;">
					<table style="width: 100%; font-size: 14px;">
						<tr v-if="getClient.phone_number">
							<td><label>Phone</label></td><td>{{ getClient.phone_number }}</td>
						</tr>
						<tr v-if="getClient.email_address">
							<td><label>Email</label></td><td>{{ getClient.email_address }}</td>
						</tr>
					</table>
				</td>
			</tr>
		</table>

		<h2 style="font-size: 1.5em; font-weight: bold;">{{ orderDetail.order.order_method_code }} Order #{{ orderDetail.order.order_number }}</h2>

		<table style="width: 100%; font-size: 14px;">
			<tr>
				<td>
					<h3>Date</h3>
					{{ orderDetail.order.display_date }}
				</td>
				<td>
					<h3>Time</h3>
					{{ orderDetail.order.display_time }}
				</td>
				<td style="text-align: right;">
					<h3>Total</h3>
					${{ orderTotal }}
				</td>
				<td style="text-align: right;">
					<h3>Payments</h3>
					${{ paymentTotal }}
				</td>
				<td style="text-align: right;">
					<h3>Balance</h3>
					${{ orderDetail.totals.balance_remaining }}
				</td>
			</tr>
		</table>

		<table style="width: 100%; font-size: 14px;">
			<tr>
				<td>
					<h3>Customer</h3>
					<div v-if="defaultCustomer">
						Default Customer
					</div>
					<div v-else>
						<div>{{ $fullName(orderDetail.billing) }}</div>
						<div v-if="orderDetail.billing.phone_number" style="margin: 0.5em 0 0 0;">
							<label>Phone</label><div>{{ orderDetail.billing.phone_number }}</div>
						</div>
						<div v-if="orderDetail.billing.email_address" style="margin: 0.5em 0.5em 0 0;">
							<label>Email</label><div>{{ orderDetail.billing.email_address }}</div>
						</div>
					</div>
				</td>
				<td>
					<h3>Billing</h3>
					<div v-if="defaultCustomer">
						Default Customer
					</div>
					<div v-else>
						<div>{{ orderDetail.billing.first_name + ' ' + orderDetail.billing.last_name }}</div>
						<div v-if="orderDetail.billing.company_name">{{ orderDetail.billing.company_name }}</div>
						<div>{{ orderDetail.billing.address_1 }}</div>
						<div>{{ orderDetail.billing.city }}, {{ orderDetail.billing.state }} {{ orderDetail.billing.zip_code }}</div>
					</div>
				</td>
				<td>
					<h3>Shipping</h3>
					<div v-if="orderDetail.shipping.full_name">
						<div>{{ orderDetail.shipping.full_name }}</div>
						<div v-if="orderDetail.shipping.company_name">{{ orderDetail.shipping.company_name }}</div>
						<div>{{ orderDetail.shipping.address_1 }}</div>
						<div>{{ orderDetail.shipping.city }}, {{ orderDetail.shipping.state }} {{ orderDetail.shipping.zip_code }}</div>
					</div>
					<div v-else>
						In store pickup
					</div>
				</td>
			</tr>
		</table>

		<div v-if="orderDetail.order.notes">
			<h3>Notes</h3>
			{{ orderDetail.order.notes }}
		</div>

		<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>

		<h3>Payments</h3>
		<table style="width: 100%; font-size: 14px;">
			<tr>
				<th>Date</th>
				<th>Type / Number</th>
				<th>Auth Code / Trans ID</th>
				<th style="text-align: right;">Amount</th>
			</tr>
			<tr v-for="(payment, index) in orderDetail.payments">
				<td>{{ payment.display_date }}</td>
				<td>
					{{ payment.card_type }} {{ payment.description }} {{ payment.amount < 0 ? 'Refund' : '' }}<br>{{ payment.reference_number }}
					<div v-if="payment.restock_fee">Refunded ${{payment.restock_fee.processed_amount }}, Restock ${{payment.restock_fee.amount }}</div>
					<!-- <div v-if="payment.rounding_amount">Rounding Amount ${{payment.rounding_amount.amount }}</div> -->
					<div v-if="payment.external_fee"><i>Note: card service fee ${{ payment.external_fee }}, total charged ${{ (parseFloat(payment.amount) + parseFloat(payment.external_fee)).toFixed(2) }}</i></div>
				</td>
				<td>{{ payment.authorization_code }}<br>{{ payment.transaction_identifier }}</td>
				<td style="text-align: right;">${{ payment.amount }}</td>
			</tr>
		</table>

		<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>
		<h3>Items</h3>
		<table style="width: 100%; font-size: 14px;">
			<tr>
				<th style="text-align: left;"></th>
				<th style="text-align: left;">Item</th>
				<th v-if="!orderDetail.exclude_cost" style="text-align: left;">Source</th>
				<th style="text-align: right;">Price</th>
				<th style="text-align: right;">Qty</th>
				<th style="text-align: right;" width="140">Ext</th>
			</tr>
			<tr v-for="(item, index) in orderDetail.items" class="cart-item-line">
				<td class="center"><img :src="item.image_src" style="max-width: 50px;"></td>
				<td>
					<label style="padding: 0 0.5em 0 0;">{{ item.upc ? 'UPC' : 'ID' }}</label>{{ item.upc || item.product_id }}
					<div v-if="item.product_type_code == 'CLASSES'">
						Class {{ item.description }}
							<div>
								<label>{{ item.other_class_datetime ? 'Dates' : 'Date' }}</label>
								{{ item.class_datetime }}
								<template v-if="item.other_class_datetime && item.other_class_datetime.length">
									<div
										v-for="(otherClass, index) in item.other_class_datetime" :key="index">
											<label>{{ item.other_class_datetime ? 'Dates' : 'Date' }}</label>
											{{ otherClass }}
									</div>
								</template>
							</div>
						<div v-for="(classItem, index) in item.class_items">
							{{ classItem.canceled ? 'Canceled:' : 'Participant:' }} {{ classItem.first_name }} {{ classItem.last_name }}<span v-if="classItem.email_address">, {{ classItem.email_address }}</span><span v-if="classItem.phone_number">, {{ classItem.phone_number }}</span>
						</div>
					</div>
					<div v-else-if="item.product_type_code == 'RANGE'">
						Range: {{ item.description }}
						<div>Date: {{ item.reservation_display_date }}</div>
					</div>
					<div v-else>{{ item.description }}</div>
					<div v-if="item.notes"><label style="padding: 0 0.5em 0 0;">Note</label>{{ checkJsonParse(item.notes) }}</div>
					<div v-if="item.discount_amount"><label style="padding: 0 0.5em 0 0;">Discount</label>${{ item.discount_amount }}, {{ item.override_reason }}</div>
					<div v-if="item.serial_number" style="padding: 0 0 0 1em;"><label>SN</label>{{ item.serial_number }}</div>
					<div v-if="item.override_notes"><label>Note / Type</label>{{ item.override_notes }}</div>
					<div v-if="item.user_name && !orderDetail.exclude_cost"><label>Manager User</label>{{ item.user_name }}</div>
					<div v-else-if="item.manager_code && !orderDetail.exclude_cost"><label>Manager Code</label>{{ item.manager_code.replace(/.(?=.)/g, "*") }}</div>
				</td>
				<td v-if="!orderDetail.exclude_cost">
					{{ item.distributor_code || 'Store' }} <label>({{ item.available }} Available)</label>
					<div v-if="item.order_cost">${{ item.order_cost }}</div>
				</td>
				<td style="text-align: right;" >${{ item.order_price }}</td>
				<td style="text-align: right;" >{{ item.quantity }}</td>
				<td style="text-align: right;" >${{ (item.quantity * item.order_price).toFixed(2) }}</td>
			</tr>
			<tr>
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>Item Subtotal</label></td>
				<td style="text-align: right;"  width="140">${{ orderDetail.totals.order_subtotal }}</td>
			</tr>
			<tr v-for="(discount, index) in orderDetail.discounts" class="cart-fee-line">
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>{{ discount.description }}</label></td>
				<td style="text-align: right;"  width="140">-${{ discount.amount }}</td>
			</tr>
			<tr v-for="(fee, index) in orderDetail.fees" class="cart-fee-line">
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>{{ fee.description }}</label></td>
				<td style="text-align: right;"  width="140">${{ fee.amount }}</td>
			</tr>
			<tr v-for="(fee, index) in orderDetail.order_item_fees" class="cart-fee-line">
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>{{ fee.description }}</label></td>
				<td style="text-align: right;"  width="140">${{ fee.amount }}</td>
			</tr>
			<tr v-if="orderDetail.totals.shipping_charge">
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>Shipping</label></td>
				<td style="text-align: right;"  width="140">${{ orderDetail.totals.shipping_charge }}</td>
			</tr>
			<tr v-if="orderDetail.totals.handling_charge">
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>Handling</label></td>
				<td style="text-align: right;"  width="140">${{ orderDetail.totals.handling_charge }}</td>
			</tr>
			<tr>
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>Sales Tax</label></td>
				<td style="text-align: right;"  width="140">${{ orderDetail.totals.tax_charge }}</td>
			</tr>
			<tr v-if ="orderDetail.totals.other_tax_amount">
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>Other Taxes / Fees</label></td>
				<td style="text-align: right;"  width="140">${{ orderDetail.totals.other_tax_amount }}</td>
			</tr>
			<tr v-if ="orderDetail.totals.cash_rounding_total">
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>Cash Rounding</label></td>
				<td style="text-align: right;"  width="140">${{ orderDetail.totals.cash_rounding_total }}</td>
			</tr>
			<tr>
				<td></td>
				<td></td>
				<td v-if="!orderDetail.exclude_cost"></td>
				<td></td>
				<td style="text-align: right;" ><label>Total</label></td>
				<td style="text-align: right;"  width="140">${{ orderTotal }}</td>
			</tr>
		</table>

	</div>
</div>

</div>

</template>

<script>
import StateSelect from '@/components/StateSelect.vue'
import ToolTip from '@/components/ToolTip.vue'
export default {
	name: 'order-detail',
	props: [ 'orderDetail', 'orderStatusCodes', 'orderSources', 'packingSlip', 'showPrint', 'editLevel', 'enableEdit' ],
	components: {
		'state-select': StateSelect,
		'tool-tip': ToolTip,
	},
	data() {
		return {
			localItems: [],
			orderStatusId: '',
			sourceId: '',
			showOrderLogModal: false,
			showMoreInfoModal: false,
			infoRequired: {},
			submittingOrder: false,
			modalMessage: '',
			fulfillmentEvents: [],
			savingNotes: false,
			savingStatus: false,
			savingSource: false,
			showOrderStatusModal: false,
			showDiscountNotes: false,
			showOtherFees: false,
			showTaxReason: false,
		}
	},
	created() {
		// localItems enables click to update in_cart, adding_to_cart
		for (const [key, item] of Object.entries(this.orderDetail.items)) {
			item.in_cart = 0
			item.adding_to_cart = 0
			this.localItems.push(item)
		}
		this.orderStatusId = this.orderDetail.order.order_status_id
		this.sourceId = this.orderDetail.order.source_id
	},
	computed: {
		defaultCustomer() {
			return this.orderDetail.order.contact_id == this.getClient.contact_id
		},
		paymentTotal() {
			let paymentTotal = parseFloat(this.orderDetail.totals.payment_total)
			if (this.orderDetail.totals.restock_fee_total) {
				paymentTotal = paymentTotal + parseFloat(this.orderDetail.totals.restock_fee_total)
			}
			if (this.orderDetail.totals.cash_rounding_total) {
				paymentTotal = paymentTotal + parseFloat(this.orderDetail.totals.cash_rounding_total)
			}
			return paymentTotal.toFixed(2)
		},
		orderTotal() {
			let orderTotal = parseFloat(this.orderDetail.totals.order_total)
			if (this.orderDetail.totals.restock_fee_total) {
				orderTotal = orderTotal + parseFloat(this.orderDetail.totals.restock_fee_total)
			}
			if (this.orderDetail.totals.cash_rounding_total) {
				orderTotal = orderTotal + parseFloat(this.orderDetail.totals.cash_rounding_total)
			}
			return orderTotal.toFixed(2)
		}
	},
	methods: {
		getPaymentTotal(payment){
			let paymentTotal = parseFloat(payment.amount)
			if (payment.restock_fee) {
				paymentTotal = paymentTotal+ parseFloat(payment.restock_fee.amount)
			}
			return paymentTotal.toFixed(2)
		},
		manageShipment() {
			// TODO: link to new shipments program when ready
			this.$router.push('/legacy/shipmentmaintenance.php?url_page=show&primary_id=' + this.orderDetail.shipping.shipment_id)
		},
		mapURL(data) {
			let mapLink = data['address_1'] + "+";
			if (data['address_2']) {
				mapLink += data['address_2'] + "+";
			}
			mapLink += data['city'] + "+";
			mapLink += data['state'] + "+";
			mapLink += data['zip_code'] + "+";
			mapLink += "&hl=en&t=m&z=11";
			return "https://maps.google.com/maps?q=" + encodeURI(mapLink)
		},
		addToCart(item) {
			if (item.adding_to_cart || item.in_cart) { return }
			item.adding_to_cart = true
			this.apiCall({ destination: 'add_to_dealer_cart', data: { product_id: item.product_id, quantity: 1 } }).then(obj => {
				item.adding_to_cart = false
				item.in_cart = 1
			}).catch(err => {
				console.log(err)
			})
		},
		getDistributorDetail(item) {
			if (!item.order_distributor_id || item.order_distributor_id < 1) { return }
			if (item.order_fulfillment_event_type_code == 'INFO_REQUIRED') {
				// populate infoRequired object with shipping info
				this.infoRequired.order_id = item.order_id
				this.infoRequired.distributor_id = item.fulfilled_distributor_id
				this.infoRequired.company_name = this.orderDetail.shipping.full_name
				this.infoRequired.address_1 = this.orderDetail.shipping.address_1
				this.infoRequired.address_2 = this.orderDetail.shipping.address_2
				this.infoRequired.city = this.orderDetail.shipping.city
				this.infoRequired.state = this.orderDetail.shipping.state
				this.infoRequired.zip_code = this.orderDetail.shipping.zip_code
				this.modalMessage = ''
				this.showMoreInfoModal = true
			} else {
				this.fulfillmentEvents = []
				this.modalMessage = 'Getting distributor order data...'
				this.showOrderLogModal = true
				this.apiCall({ destination: 'get_list_order_fulfillment_events', data: { order_id: item.order_id, distributor_id: item.fulfilled_distributor_id } }).then(obj => {
					this.fulfillmentEvents = obj.order_fulfillment_events
					this.modalMessage = ''
				}).catch(err => {
					console.log(err)
				})
			}
		},
		setOrderStatus() {
			if (this.savingStatus) { return }
			this.savingStatus = true
			this.apiCall({ destination: 'save_item_order_status', data: { order_id: this.orderDetail.order.order_id, order_status_id: this.orderStatusId } }).then(obj => {
				this.savingStatus = false
				this.showOrderStatusModal = false
			}).catch(err => {
				console.log(err)
			})
		},
		setOrderSource() {
			if (this.savingSource || !this.sourceId) { return }
			this.savingSource = true
			this.apiCall({ destination: 'save_item_order_source', data: { order_id: this.orderDetail.order.order_id, source_id: this.sourceId } }).then(obj => {
				this.savingSource = false
			}).catch(err => {
				console.log(err)
			})
		},
		saveOrderNotes() {
			if (this.savingNotes) { return }
			this.savingNotes = true
			this.apiCall({ destination: 'save_item_order_notes', data: { order_id: this.orderDetail.order.order_id, notes: this.orderDetail.order.notes } }).then(obj => {
				this.savingNotes = false
			}).catch(err => {
				console.log(err)
			})
		},
		updateSelectedState(state) {
			this.infoRequired.state = state
		},
		checkJsonParse(value) {
			if (!value ) { return value }
			try {
				const parsed = JSON.parse(value)
				if(Array.isArray(parsed)){
					return parsed.map(obj => 
						Object.entries(obj).map(([key, val]) => `${key}: ${val}`).join(', ')
					).join(' | ')
				} else if(typeof parsed === 'object') {
					return Object.entries(parsed).map(([key, val]) => `${key}: ${val}`).join(', ')
				}
				return parsed
			} catch (e) {
				return value
			}
		}, 
		submitInfoRequested() {
			if (this.submittingOrder) { return }
			if (!this.infoRequired.company_name || !this.infoRequired.address_1 || !this.infoRequired.city || !this.infoRequired.state || !this.infoRequired.zip_code) {
				this.modalMessage = 'Please enter all FFL name and address info.'
				return
			}
			if (!this.infoRequired.phone_number || !this.infoRequired.ffl_number) {
				this.modalMessage = 'Please enter all FFL info.'
				return
			}
			this.modalMessage = 'Submitting distributor order...'
			this.submittingOrder = true
			this.apiCall({ destination: 'save_item_requires_info_order', data: this.infoRequired }).then(obj => {
				this.modalMessage = obj.message
				if (obj.status == 'success') {
                    setTimeout(() => {
						this.modalMessage = ''
						this.submittingOrder = false
						this.showMoreInfoModal = false
						this.$emit('reload')
                    }, 2000)
				} else {
					this.submittingOrder = false
				}
			}).catch(err => {
				console.log(err)
			})
		}
	}
}
</script>
